import React, { useState, useEffect } from "react";
import { Power, PowerOff, Loader2 } from "lucide-react";
import {
  Search,
  PlusCircle,
  Upload,
  Download,
  Filter,
  Edit,
  Trash,
  Users,
  HardHat,
  Building2,
} from "lucide-react";
import { Input } from "@shared/ui/input";
import { Button } from "@shared/ui/button";
import { Card } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { Switch } from "@shared/ui/switch";
import { useToast } from "@shared/hooks/use-toast";
import { useNavigate, useSearchParams } from "react-router-dom";

import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { ListFilterPanel } from "../filters/index"; // Import the filter panel

import {
  ContactList,
  ListType,
  ListTypeFilter,
  ListFilterType,
  RecipientList,
  ContactPreviewItem,
} from "../../types";

import {
  useRecipientLists,
  useCreateRecipientList,
  useToggleListStatus,
  useDownloadCSV,
  useCommunicationListsExport,
} from "../../hooks";
import { useAuth } from "@features/auth/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { PAGE_SIZE } from "@shared/utils/constants";
import { logEvent } from "@shared/analytics/analytics";
import {
  useRemoteUtilityId,
  useUserId,
} from "@shared/selectors/globalSelectors";

interface ListsManagerProps {
  filterType?: ListFilterType | "all";
  listOwnership?: "my-lists" | "all-lists";
}

// Define the ListFilters interface here to fix TypeScript error
interface ListFilters {
  page: number;
  limit: number;
  recipientType: "CONSUMER" | "TECHNICIAN" | "BUSINESS";
  filterType?: "STATIC" | "DYNAMIC";
  isActive?: boolean[];
  search_data?: string;
  listOwnership: "my-lists" | "all-lists";
}

export const Manager: React.FC<ListsManagerProps> = ({
  filterType = "all",
  listOwnership = "my-lists",
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [togglingList, setTogglingList] = useState<number | null>(null);

  const itemsPerPage = PAGE_SIZE;

  // Parse filters from URL params
  const getFiltersFromUrl = (): ListFilters => {
    const page = parseInt(searchParams.get("page") || "1");
    const recipientTypeParam = searchParams.get("recipientType");

    const urlFilters: ListFilters = {
      page,
      limit: itemsPerPage,
      recipientType: recipientTypeParam as
        | "CONSUMER"
        | "TECHNICIAN"
        | "BUSINESS",
      listOwnership,
    };

    // Parse filter type
    const filterTypeParam = searchParams.get("filterType");
    if (filterTypeParam && filterTypeParam !== "ALL_FILTER_TYPES") {
      urlFilters.filterType = filterTypeParam as "STATIC" | "DYNAMIC";
    }

    // Parse status array
    const statusParam = searchParams.get("status");
    if (statusParam && statusParam !== "ALL_STATUS") {
      urlFilters.isActive = statusParam.split(",").map((s) => s === "true");
    }

    // Parse search query
    const queryParam = searchParams.get("query");
    if (queryParam) {
      urlFilters.search_data = queryParam;
    }

    return urlFilters;
  };

  const [filters, setFilters] = useState<ListFilters>(getFiltersFromUrl);

  // Update filters when URL changes
  useEffect(() => {
    const newFilters = getFiltersFromUrl();
    setFilters(newFilters);
  }, [searchParams, listOwnership]);

  // Use the recipient lists hook with filters - NOW WITH SERVER-SIDE FILTERING
  const { data: recipientListsResponse, refetch } = useRecipientLists(filters); // Pass entire filters object
  const { exportCSV, isExporting } = useCommunicationListsExport(
    useRemoteUtilityId() || "", // Adjust based on your user structure
    {
      recipientType: filters.recipientType,
      filterType: filters.filterType,
      isActive: filters.isActive,
      search_data: filters.search_data,
      userId: listOwnership === "my-lists" ? useUserId() : undefined,
    }
  );

  const handleExportAll = () => {
    exportCSV();
  };

  const toggleListMutation = useToggleListStatus();
  const createListMutation = useCreateRecipientList();
  const downloadCSV = useDownloadCSV();

  // NO MORE CLIENT-SIDE FILTERING - data comes filtered from server
  const tableData = recipientListsResponse?.results || [];
  const totalItems = recipientListsResponse?.count || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleApplyFilters = (newFilters: Partial<ListFilters>) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("recipientType");
    params.delete("filterType");
    params.delete("status");
    params.delete("page");

    // Set new filter params
    if (newFilters.recipientType) {
      params.set("recipientType", newFilters.recipientType);
    }

    if (newFilters.filterType) {
      params.set("filterType", newFilters.filterType);
    }

    if (newFilters.isActive && newFilters.isActive.length > 0) {
      params.set(
        "status",
        newFilters.isActive.map((s) => s.toString()).join(",")
      );
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("recipientType");
    params.delete("filterType");
    params.delete("status");
    params.delete("page");
    params.delete("query");

    setSearchParams(params);
  };
  useEffect(() => {
    refetch(); // Just refetch the current query
  }, [location.pathname]);

  const handleToggleListStatus = async (list: any) => {
    setTogglingList(list.id);

    try {
      await toggleListMutation.mutateAsync(
        {
          listId: list.id,
          isActive: !list.isActive,
        },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries({
              predicate: (query) => {
                const key = query.queryKey;
                return (
                  Array.isArray(key) && key[0] === "communicationRecipientList"
                );
              },
            });
          },
        }
      );
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update list status",
        variant: "destructive",
      });
    } finally {
      setTogglingList(null);
    }
  };

  const handleExport = (listId: number) => {
    downloadCSV.mutateAsync({ taskID: listId });
  };

  const getListTypeIcon = (type: ListType) => {
    switch (type) {
      case "CONSUMER":
        return <Users className="h-4 w-4 mr-2" />;
      case "TECHNICIAN":
        return <HardHat className="h-4 w-4 mr-2" />;
      case "BUSINESS":
        return <Building2 className="h-4 w-4 mr-2" />;
      default:
        return null;
    }
  };

  // Define table columns
  const columns: TableColumn<RecipientList>[] = [
    {
      key: "name",
      header: "List Name",
      width: "w-64",
      type: "custom",
      render: (_, row) => (
        <div>
          <div className="flex items-center gap-2 font-medium">
            {getListTypeIcon(row.recipientType)}
            <span>{row.name}</span>
          </div>
          <div className="mt-1 space-y-1">
            <Badge variant="outline" size="sm" className="capitalize">
              {row.filterType === "STATIC" ? "Static" : "Dynamic"}
            </Badge>
            <div className="text-xs text-muted-foreground">
              <div>Created by: {row.createdUserRemoteName}</div>
              <div>Updated by: {row.lastModifiedUserRemoteName}</div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "description",
      header: "Description",
      width: "w-48 hidden md:table-cell",
      type: "custom",
      render: (_, row) => (
        <span className="max-w-xs truncate block">{row.description}</span>
      ),
    },
    {
      key: "totalRecipients",
      header: "Contacts",
      width: "w-24 hidden sm:table-cell",
      type: "custom",
      render: (_, row) => <span>{row.totalRecipients ?? "NA"}</span>,
    },
    {
      key: "createdDate",
      header: "Created",
      width: "w-32 hidden lg:table-cell",
      type: "custom",
      render: (_, row) => {
        // Format the date if needed
        const date = new Date(row.createdDate);
        return (
          <span>
            {date.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
        );
      },
    },
    {
      key: "tags",
      header: "Tags",
      width: "w-48 hidden xl:table-cell",
      type: "custom",
      render: (_, row) => (
        <div className="flex flex-wrap gap-1">
          {row.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
            >
              {tag}
            </span>
          ))}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "w-24 hidden sm:table-cell",
      type: "custom",
      render: (_, row) => (
        <Badge
          variant={row.isActive ? "default" : "secondary"}
          className={
            row.isDraft
              ? "bg-red-100 text-red-800 border-red-200"
              : row.isActive
              ? "bg-green-100 text-green-800 border-green-200"
              : "bg-red-100 text-red-800 border-red-200"
          }
        >
          {row.isDraft ? "Draft" : row.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      width: "w-32",
      type: "custom",
      render: (_, row) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              handleExport(row.id);
              logEvent("Clicked List Actions", {
                actionType: "Export",
                listId: row.id,
              });
            }}
            title="Export List"
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              console.log("debug edit clicked with id", row.id);
              logEvent("Clicked List Actions", {
                actionType: "Edit",
                listId: row.id,
              });
              navigate(`/communication-hub/lists/edit/${row.id}`);
            }}
            title="Edit List"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Switch
            checked={row.isActive}
            onCheckedChange={(checked) => {
              logEvent("Clicked List Actions", {
                actionType: "Toggle",
                listId: row.id,
              });
              // If trying to deactivate (checked = false) and list is in use
              if (!checked && row.usageCount > 0) {
                toast({
                  title: "Cannot Deactivate List",
                  description:
                    "This list is currently in use and cannot be deactivated.",
                  variant: "destructive",
                });
                return; // Prevent the toggle action
              }

              // Otherwise, proceed with the toggle
              handleToggleListStatus(row);
            }}
            disabled={togglingList === row.id || row.isDraft}
          />
        </div>
      ),
    },
  ];

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  // Handle search
  const handleSearch = (searchTerm: string, paramName: string) => {
    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }

    // Reset to page 1 when searching
    params.set("page", "1");
    setSearchParams(params);
  };

  const currentPage = parseInt(searchParams.get("page") || "1");

  return (
    <div className="space-y-4">
      {/* Generic Table with Server-Side Filtering */}
      <GenericTable
        data={tableData} // Server-filtered data
        columns={columns}
        searchPlaceholder="Search lists..."
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        onExport={handleExportAll} // Add this line
        exportLoading={isExporting}
        rowKey="id"
        pagination={{
          currentPage: currentPage,
          totalPages: totalPages, // From server response
          totalItems: totalItems, // From server response
          itemsPerPage,
          startItem: (currentPage - 1) * itemsPerPage + 1,
          endItem: Math.min(currentPage * itemsPerPage, totalItems),
        }}
        onPageChange={handlePageChange}
        filterPanel={
          <ListFilterPanel
            currentFilters={filters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        }
        emptyMessage={`No lists found${
          filters.search_data ? ` matching "${filters.search_data}"` : ""
        }`}
      />
    </div>
  );
};
